import axios from 'axios';
import api from '@/lib/axios';
import { documentId, DOCUMENT_PERMISSIONS, validateDocumentFile, validateDocumentMetadata } from './documentPresentation';

// The general api instance retries transient errors, including POSTs.
// Document uploads have no server idempotency key yet: use a dedicated
// cookie-authenticated client with NO automatic retries or route fallbacks.
const client = axios.create({
  baseURL: api.defaults.baseURL,
  withCredentials: true,
  timeout: 120000,
  headers: { Accept: 'application/json' },
});
function entityPath(type, id) {
  if (!Object.hasOwn(DOCUMENT_PERMISSIONS, type)) throw new Error('Unsupported document record.');
  return `/documents/entity/${type}/${documentId(id)}`;
}
export async function listEntityDocuments(type, id, signal, companyId) {
  const { data } = await client.get(entityPath(type, id), {
    signal, timeout: 20000, params: { _ts: Date.now() },
  });
  if (!Array.isArray(data?.documents)) throw new Error('The document list could not be read.');
  const expected = documentId(id);
  for (const row of data.documents) {
    if (row.entity_type !== type || String(row.entity_id) !== expected ||
      (companyId != null && String(row.company_id) !== String(companyId))) {
      throw new Error('Document context changed. Close this panel and reopen the current record.');
    }
  }
  return data.documents;
}
export async function uploadEntityDocument(type, id, file, values, onUploadProgress) {
  entityPath(type, id);
  const mime = validateDocumentFile(file);
  validateDocumentMetadata(values);
  const body = new FormData();
  body.append('entity_type', type);
  body.append('entity_id', documentId(id));
  for (const name of ['document_type', 'title', 'description', 'version_label', 'effective_date', 'expiry_date', 'review_date']) {
    if (values[name]) body.append(name, String(values[name]).trim());
  }
  body.append('file', file.slice(0, file.size, mime), file.name);
  // Do not set Content-Type: the browser must provide its multipart boundary.
  const response = await client.post('/documents/upload', body, { onUploadProgress });
  if (response.status !== 201 || !response.data?.document_id) {
    throw new Error('Upload result not confirmed. Refresh the list before trying again.');
  }
  return response.data;
}
export async function retrieveDocumentFile(id, signal) {
  const response = await client.get(`/documents/${documentId(id)}/file`, {
    responseType: 'blob', signal, params: { _ts: Date.now() },
  });
  if (!(response.data instanceof Blob)) throw new Error('The document download could not be read.');
  return response.data;
}
export function documentError(error) {
  const status = error?.response?.status;
  if (status === 401) return 'Your session is not available. Sign in again before continuing.';
  if (status === 403) return 'You do not have permission for this document action.';
  if (status === 404) return 'This record or document is not available in the selected company and unit.';
  if (status === 413) return 'The file exceeds the server upload limit. Choose a smaller file.';
  if (status === 400 || status === 422) return 'The server rejected the file or its details. Check the file type and dates.';
  if (status === 503) return 'The document service is not available yet. Try again later.';
  if (axios.isAxiosError(error)) return 'The request could not be confirmed. Check your connection and refresh the list.';
  return error?.message || 'The document request could not be completed.';
}
